import { useRef, useState } from "react";
import { Link } from "@/i18n/LocaleLink";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT, useContent } from "@/data/content";
import { THAI_MASSAGE } from "@/data/thaiMassage";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const BookButton = ({ options, testid, center = false }) => (
  <span className={`flex flex-wrap gap-4 ${center ? "justify-center" : ""}`}>
    {options.map((o) => (
      <a
        key={o.label}
        href={o.label.trim().startsWith("120") ? CONTACT.massage120Url : CONTACT.massage90Url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testid}-${o.label.split(" ")[0]}`}
        className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
      >
        {o.label} — {o.price}
        <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    ))}
  </span>
);

const PricePair = ({ options, dark, idPrefix = "tm-option" }) => (
  <div className="flex flex-wrap gap-x-10 gap-y-2">
    {options.map((o) => (
      <p key={o.label} className={`font-serif text-2xl sm:text-3xl ${dark ? "text-cream" : "text-ink"}`} data-testid={`${idPrefix}-${o.label.split(" ")[0]}`}>
        {o.label} <span className={dark ? "font-medium text-sand" : "text-clay"}>— {o.price}</span>
      </p>
    ))}
  </div>
);

const Hero = ({ tm }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden grain" data-testid="tm-hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-32">
        <img src="/images/thai-massage.jpg" alt="Fully clothed Thai massage on a floor mat in a warm home setting" className="h-full w-full object-cover scale-105" />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-ink/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-20 pt-40 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mb-6 max-w-xl text-xs tracking-[0.3em] uppercase text-cream/85"
          data-testid="tm-hero-eyebrow"
        >
          {tm.hero.eyebrow}
        </motion.p>
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl"
            data-testid="tm-hero-title"
          >
            {tm.hero.title}
          </motion.h1>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-8"
          data-testid="tm-hero-facts"
        >
          <PricePair options={tm.hero.options} dark idPrefix="tm-hero-option" />
          <div className="mt-4 hidden sm:flex flex-wrap items-center gap-x-5 gap-y-2 text-sm tracking-wide text-sand">
            {tm.hero.facts.map((f, i) => (
              <span key={f} className="flex items-center gap-5">
                {i > 0 && <span className="text-cream/40">·</span>}
                <span>{f}</span>
              </span>
            ))}
          </div>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-cream"
          data-testid="tm-hero-intro"
        >
          {tm.hero.sub} {tm.hero.intro}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-9 flex flex-wrap items-center gap-6"
        >
          <BookButton options={tm.hero.options} testid="tm-hero-book" />
          <a href="#booking" data-testid="tm-hero-options"
            className="group inline-flex items-center gap-2 text-sm tracking-wide text-cream/85 hover:text-cream transition-colors duration-300 border-b border-cream/40 pb-1">
            {tm.hero.secondaryCta}
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ink/10" data-testid={`tm-faq-${index}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-6 py-5 text-left" data-testid={`tm-faq-toggle-${index}`}>
        <span className="font-serif text-lg sm:text-xl text-ink">{item.q}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <p className="pb-6 pr-10 text-base leading-relaxed text-soft">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function ThaiMassagePage() {
  const { lang } = useLang();
  const { offerings } = useContent();
  const tm = THAI_MASSAGE[lang] || THAI_MASSAGE.en;
  useSeo({ ...tm.seo, image: `/images/thai-massage.jpg` });
  const topicName = offerings.find((o) => o.slug === "thai-massage")?.name || "Pregnancy & Postnatal Thai Massage";
  const contactLink = `/contact?topic=${encodeURIComponent(topicName)}`;

  return (
    <main data-testid="thai-massage-page">
      <Hero tm={tm} />

      <section className="bg-cream py-24 lg:py-36" data-testid="tm-care">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] tracking-tight text-ink">{tm.care.title}</h2>
            <p className="mt-8 text-base leading-relaxed text-soft drop-cap">{tm.care.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{tm.care.p2}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-14 font-serif text-3xl sm:text-4xl leading-[1.25] tracking-tight text-clay text-center" data-testid="tm-care-pull">{tm.care.pull}</p>
            <p className="mt-14 text-base leading-relaxed text-soft">{tm.care.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="tm-home">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-4">{tm.home.heading}</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{tm.home.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{tm.home.p1}</p>
            <p className="mt-5 font-serif text-2xl leading-snug text-clay italic">{tm.home.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{tm.home.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{tm.home.p4}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
            <div className="overflow-hidden rounded-t-[10rem]">
              <img loading="lazy" src="/images/postpartum.jpg" alt="A warm, unhurried home setting with tea and candlelight" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28" data-testid="tm-feels">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="overflow-hidden rounded-t-[10rem]">
              <img loading="lazy" src="/images/thai-massage.jpg" alt="Hands giving rhythmic pressure during a clothed Thai massage on a floor mat" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{tm.feels.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{tm.feels.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{tm.feels.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{tm.feels.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{tm.feels.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-16 lg:py-20" data-testid="tm-testimonials">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{tm.testimonials.heading}</h2>
            <div className="mt-12 text-left max-w-2xl mx-auto">
              {tm.testimonials.items.map((t, i) => (
                <blockquote key={i} data-testid={`tm-testimonial-${i}`}>
                  <p className="font-serif italic text-5xl leading-none text-clay/40" aria-hidden>"</p>
                  <p className="mt-2 font-serif italic text-lg leading-relaxed text-ink/85 whitespace-pre-line">{t.text}</p>
                  <footer className="mt-5 text-xs tracking-[0.25em] uppercase text-clay">— {t.name}</footer>
                </blockquote>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28" data-testid="tm-wholebody">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{tm.wholeBody.title}</h2>
            <p className="mt-7 font-serif text-2xl leading-snug text-clay italic">{tm.wholeBody.p1}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{tm.wholeBody.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{tm.wholeBody.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{tm.wholeBody.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-16 lg:py-20" data-testid="tm-quiet">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{tm.quiet.title}</h2>
            <p className="mt-6 text-sm leading-relaxed text-soft">{tm.quiet.p1}</p>
            <p className="mt-8 font-serif italic text-xl sm:text-2xl leading-snug text-clay" data-testid="tm-quiet-pull">{tm.quiet.pull}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28" data-testid="tm-baby">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{tm.baby.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{tm.baby.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{tm.baby.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{tm.baby.p3}</p>
            <p className="mt-8 font-serif italic text-xl leading-snug text-ink/80">{tm.baby.closing}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-16 lg:py-20" data-testid="tm-when">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{tm.when.title}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft">{tm.when.p1}</p>
            <p className="mt-4 text-sm leading-relaxed text-soft">{tm.when.p2}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28" data-testid="tm-therapist">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-4 rounded-t-[10rem] border border-clay/30" aria-hidden />
              <div className="overflow-hidden rounded-t-[10rem]">
                <img loading="lazy" src="/irida.jpg" alt="Irida Hysenbelli, Thai massage therapist and founder of Women's Den" className="aspect-[3/4] w-full object-cover" data-testid="tm-therapist-photo" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{tm.therapist.heading}</h2>
            <p className="mt-4 text-xs tracking-[0.25em] uppercase text-clay" data-testid="tm-therapist-qual">{tm.therapist.qual}</p>
            <p className="mt-6 text-base leading-relaxed text-soft drop-cap">{tm.therapist.body}</p>
            <Link to="/about" data-testid="tm-therapist-link"
              className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
              {tm.therapist.link}
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-16 lg:py-24" data-testid="tm-faq">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-6">{tm.faq.heading}</h2>
          </Reveal>
          <Reveal>
            <div>
              {tm.faq.items.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="booking" className="bg-ink py-24 lg:py-32 grain relative" data-testid="tm-final">
        <div className="mx-auto max-w-3xl px-6 text-center relative z-10">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-cream">{tm.final.title}</h2>
            <p className="mt-10 text-xs tracking-[0.3em] uppercase text-sand/80">{tm.final.heading}</p>
            <div className="mt-6 flex justify-center" data-testid="tm-final-options">
              <PricePair options={tm.final.options} dark idPrefix="tm-final-option" />
            </div>
            <p className="mt-5 text-sm text-cream/70">{tm.final.line1}</p>
            <p className="mt-1 text-sm text-cream/70">{tm.final.line2}</p>
            <div className="mt-10 flex justify-center">
              <BookButton options={tm.final.options} testid="tm-final-book" center />
            </div>
            <Link to={contactLink} data-testid="tm-final-contact"
              className="group mt-8 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
              {tm.final.secondary}
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
